import React, { useState } from 'react';
import { BiSearch, BiRefresh, BiCopy, BiCheck, BiCreditCard, BiBuildingHouse, BiDownload } from 'react-icons/bi';
import { ThemeContext } from '../../themes/ThemeContext';
import { getAccountStatus, checkSettlementStatus } from '../../data/api/analytics';
import { exportAccountStatusPDF, exportSettlementStatusPDF } from '../../utils/pdfExport';

const AccountStatusSearch = ({ className = '' }) => {
  const theme = React.useContext(ThemeContext);
  
  // Account Status State
  const [orderId, setOrderId] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  
  // Settlement Status State
  const [transactionRef, setTransactionRef] = useState('');
  const [settlementResult, setSettlementResult] = useState(null);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [settlementError, setSettlementError] = useState(null);
  const [sourceTable, setSourceTable] = useState(null);
  const [settlementPdfExporting, setSettlementPdfExporting] = useState(false);

  const handleSearch = async () => {
    if (!orderId.trim()) {
      setError('Please enter an Order ID');
      return;
    }

    setLoading(true);
    setError(null);
    setSearchResult(null);

    try {
      const result = await getAccountStatus(orderId.trim());
      setSearchResult(result);
    } catch (err) {
      setError(err.message || 'Network error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setSearchResult(null);
    setError(null);
    setOrderId('');
  };

  const handleSettlementStatusCheck = async () => {
    if (!transactionRef.trim()) {
      setSettlementError('Please enter a transaction reference or provider payment ID');
      return;
    }

    setSettlementLoading(true);
    setSettlementError(null);
    setSettlementResult(null);
    setSourceTable(null);

    try {
      const input = transactionRef.trim();
      
      // Auto-detect if input is a provider payment ID (UUID format) or transaction reference
      const isProviderPaymentId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(input);
      
      const searchParams = isProviderPaymentId 
        ? { providerPaymentId: input, transactionReference: null }
        : { transactionReference: input, providerPaymentId: null };

      const result = await checkSettlementStatus(searchParams);

      // Handle structured responses (both success and error cases)
      if (result.status === true) {
        // Success case - transaction found
        setSettlementResult(result);
        setSourceTable(result.source_table);
      } else {
        // Error case - transaction not found or other error
        setSettlementResult(result);
        setSourceTable(null);
      }
    } catch (err) {
      setSettlementError(err.message || 'Network error occurred. Please try again.');
    } finally {
      setSettlementLoading(false);
    }
  };

  const handleSettlementRefresh = () => {
    setSettlementResult(null);
    setSettlementError(null);
    setTransactionRef('');
    setSourceTable(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAccountStatusPDFExport = async () => {
    if (!searchResult?.orderId) return;
    
    setPdfExporting(true);
    try {
      await exportAccountStatusPDF(searchResult.orderId, searchResult);
    } catch (error) {
      setError(`PDF export failed: ${error.message}`);
    } finally {
      setPdfExporting(false);
    }
  };

  const handleSettlementStatusPDFExport = async () => {
    if (!settlementResult?.data?.transaction_reference && !settlementResult?.data?.provider_payment_id) return;
    
    setSettlementPdfExporting(true);
    try {
      const transactionRef = settlementResult.data.transaction_reference;
      const providerPaymentId = settlementResult.data.provider_payment_id;
      
      await exportSettlementStatusPDF(transactionRef, providerPaymentId, settlementResult);
    } catch (error) {
      setSettlementError(`PDF export failed: ${error.message}`);
    } finally {
      setSettlementPdfExporting(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-NG');
  };

  const getSettlementStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#10b981';
      case 'processing': return '#f59e0b';
      case 'failed': return '#ef4444';
      case 'not_found': return '#f59e0b';
      case 'server_error': return '#ef4444';
      case 'unauthorized': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getSettlementStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return '✅';
      case 'processing': return '⏳';
      case 'failed': return '❌';
      case 'not_found': return '🔍';
      case 'server_error': return '⚠️';
      case 'unauthorized': return '🔒';
      default: return '❓';
    }
  };

  const getSourceTableLabel = (sourceTable) => {
    switch (sourceTable) {
      case 'bank_transactions': return 'Confirmed Transaction';
      case 'pending_transactions': return 'Pending Transaction';
      case 'failed_transactions': return 'Failed Transaction';
      default: return 'Unknown Source';
    }
  };

  const extractTransactionReference = (fullReference) => {
    if (fullReference && fullReference.includes('|')) {
      return fullReference.split('|')[0];
    }
    return fullReference;
  };

  const formatTransactionReference = (reference) => {
    if (reference && reference.includes('|')) {
      const parts = reference.split('|');
      return {
        main: parts[0],
        suffix: parts[1],
        hasSuffix: true
      };
    }
    return {
      main: reference,
      suffix: null,
      hasSuffix: false
    };
  };

  return (
    <div className={`w-full ${className}`}>
      <div className="space-y-8">
        {/* Account Status Card */}
        <div className="rounded-lg shadow-sm" style={{ backgroundColor: theme.colors.background.secondary }}>
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 rounded-lg" style={{ backgroundColor: theme.colors.background.accent }}>
                <BiCreditCard className="w-6 h-6" style={{ color: theme.colors.text.primary }} />
              </div>
              <div>
                <h3 className="text-lg font-semibold" style={{ color: theme.colors.text.primary }}>Account Status Search</h3>
                <p className="text-sm" style={{ color: theme.colors.text.primary + '80' }}>Search for transaction details by order ID</p>
              </div>
            </div>

            <div className="flex space-x-3 mb-6">
              <div className="flex-1">
                <input
                  type="text"
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  placeholder="Enter Order ID (e.g., 6aeea511-2d31-46d6-88ac-120cc3363922)"
                  className=" text-sm w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  style={{ 
                    backgroundColor: theme.colors.background.primary,
                    borderColor: theme.colors.background.accent,
                    color: theme.colors.text.primary
                  }}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={loading || !orderId.trim()}
                className="px-6 py-3 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-all duration-200 hover:shadow-lg"
                style={{ background: theme.colors.background.buttonBg, zIndex: 1 }}
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <BiSearch className="w-4 h-4" />
                )}
                <span>{loading ? 'Searching...' : 'Search'}</span>
              </button>
            </div>

            {error && (
              <div className="mb-6 p-4 border rounded-lg" style={{ 
                backgroundColor: theme.colors.background.accent,
                borderColor: '#dc2626'
              }}>
                <p className="text-sm" style={{ color: '#ef4444' }}>{error}</p>
              </div>
            )}

            {searchResult && (
              <div className="space-y-6 mt-16">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold" style={{ color: theme.colors.text.primary }}>Transaction Details</h4>
                  <div className="flex space-x-2">
                    {/* Show download button for successful transactions */}
                    {searchResult.status && (
                      <button
                        onClick={handleAccountStatusPDFExport}
                        disabled={pdfExporting}
                        className="px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-all duration-200 hover:shadow-lg"
                        style={{ background: theme.colors.background.buttonBg }}
                        title="Download Receipt"
                      >
                        {pdfExporting ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <BiDownload className="w-4 h-4" />
                        )}
                        <span>{pdfExporting ? 'Downloading...' : 'Download Receipt'}</span>
                      </button>
                    )}
                    <button
                      onClick={handleRefresh}
                      className="p-2 rounded-lg hover:bg-gray-700 transition-colors"
                      style={{ color: theme.colors.text.primary }}
                      title="New Search"
                    >
                      <BiRefresh className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.primary }}>
                      <label className="block text-sm font-medium mb-1" style={{ color: theme.colors.text.primary + '80' }}>Order ID</label>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-mono" style={{ color: theme.colors.text.primary }}>{searchResult.orderId}</span>
                        <button
                          onClick={() => copyToClipboard(searchResult.orderId)}
                          className="p-1 hover:bg-gray-700 rounded transition-colors"
                          style={{ color: theme.colors.text.primary + '80' }}
                          title="Copy Order ID"
                        >
                          {copied ? <BiCheck className="w-3 h-3" style={{ color: '#10b981' }} /> : <BiCopy className="w-3 h-3" />}
                        </button>
                      </div>
                    </div>

                    <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.primary }}>
                      <label className="block text-sm font-medium mb-1" style={{ color: theme.colors.text.primary + '80' }}>Status</label>
                      <span className={`text-lg font-bold px-3 py-1 rounded-full text-sm ${
                        searchResult.status === 'success' ? 'bg-green-100 text-green-800' :
                        searchResult.status === 'failed' ? 'bg-red-100 text-red-800' :
                        searchResult.status === 'reversed' ? 'bg-orange-100 text-orange-800' :
                        searchResult.status === 'not_found' ? 'bg-yellow-100 text-yellow-800' :
                        searchResult.status === 'pending' ? 'bg-blue-100 text-blue-800' :
                        searchResult.status === 'server_error' ? 'bg-red-100 text-red-800' :
                        searchResult.status === 'unauthorized' ? 'bg-orange-100 text-orange-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {searchResult.status === 'success' ? 'Success' :
                         searchResult.status === 'failed' ? 'Failed' :
                         searchResult.status === 'reversed' ? 'Reversed' :
                         searchResult.status === 'not_found' ? 'Not Found' :
                         searchResult.status === 'pending' ? 'Pending' :
                         searchResult.status === 'server_error' ? 'Server Error' :
                         searchResult.status === 'unauthorized' ? 'Unauthorized' :
                         searchResult.status || 'Unknown'}
                      </span>
                    </div>

                    <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.primary }}>
                      <label className="block text-sm font-medium mb-1" style={{ color: theme.colors.text.primary + '80' }}>Amount</label>
                      <span className="text-lg font-bold" style={{ color: '#10b981' }}>
                        {(() => {
                          const amount = searchResult.status === 'reversed' 
                            ? searchResult.data?.data?.amount_paid 
                            : searchResult.data?.data?.amount;
                          return amount === "N/A" || amount === null || amount === undefined ? "N/A" : formatAmount(amount);
                        })()}
                      </span>
                    </div>

                    <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.primary }}>
                      <label className="block text-sm font-medium mb-1" style={{ color: theme.colors.text.primary + '80' }}>Merchant ID</label>
                      <span className="text-sm font-mono" style={{ color: theme.colors.text.primary }}>{searchResult.data?.data?.merchantId || "N/A"}</span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.primary }}>
                      <label className="block text-sm font-medium mb-1" style={{ color: theme.colors.text.primary + '80' }}>Payer Name</label>
                      <span className="text-sm" style={{ color: theme.colors.text.primary }}>{searchResult.data?.data?.payer_account_name || "N/A"}</span>
                    </div>

                    <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.primary }}>
                      <label className="block text-sm font-medium mb-1" style={{ color: theme.colors.text.primary + '80' }}>Payer Bank</label>
                      <span className="text-sm" style={{ color: theme.colors.text.primary }}>{searchResult.data?.data?.payer_bank || "N/A"}</span>
                    </div>

                    <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.primary }}>
                      <label className="block text-sm font-medium mb-1" style={{ color: theme.colors.text.primary + '80' }}>Payment Date</label>
                      <span className="text-sm" style={{ color: theme.colors.text.primary }}>
                        {searchResult.data?.data?.paid_at === "N/A" ? "N/A" : formatDate(searchResult.data?.data?.paid_at)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.primary }}>
                  <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary + '80' }}>Transaction Details</label>
                  <p className="text-sm leading-relaxed" style={{ color: theme.colors.text.primary }}>{searchResult.data?.data?.details || "N/A"}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Settlement Status Card */}
        <div className="rounded-lg shadow-sm" style={{ backgroundColor: theme.colors.background.secondary }}>
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 rounded-lg" style={{ backgroundColor: theme.colors.background.accent }}>
                <BiBuildingHouse className="w-6 h-6" style={{ color: theme.colors.text.primary }} />
              </div>
              <div>
                <h3 className="text-lg font-semibold" style={{ color: theme.colors.text.primary }}>Settlement Status Search</h3>
                <p className="text-sm" style={{ color: theme.colors.text.primary + '80' }}>Check settlement status with enhanced error details and support for all transaction formats</p>
              </div>
            </div>

            <div className="flex space-x-3 mb-6">
              <div className="flex-1">
                <input
                  type="text"
                  value={transactionRef}
                  onChange={(e) => setTransactionRef(e.target.value)}
                  placeholder="Enter Transaction Reference or Provider Payment ID (e.g., NIPEXT202410261673 or a8a448b6-a325-4d7d-84b7-7c53928dd0b5)"
                  className=" text-sm w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  style={{ 
                    backgroundColor: theme.colors.background.primary,
                    borderColor: theme.colors.background.accent,
                    color: theme.colors.text.primary
                  }}
                  onKeyPress={(e) => e.key === 'Enter' && handleSettlementStatusCheck()}
                />
              </div>
            </div>
            <div className="flex justify-left mb-6">
              <button
                onClick={handleSettlementStatusCheck}
                disabled={settlementLoading || !transactionRef.trim()}
                className="px-8 py-3 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-all duration-200 hover:shadow-lg"
                style={{ background: theme.colors.background.buttonBg }}
              >
                {settlementLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <BiSearch className="w-4 h-4" />
                )}
                <span>{settlementLoading ? 'Checking...' : 'Check Settlement Status'}</span>
              </button>
            </div>

            {settlementError && (
              <div className="mb-6 p-4 border rounded-lg" style={{ 
                backgroundColor: theme.colors.background.accent,
                borderColor: '#dc2626'
              }}>
                <p className="text-sm" style={{ color: '#ef4444' }}>{settlementError}</p>
              </div>
            )}

            {settlementResult && (
              <div className="space-y-6 mt-16">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold" style={{ color: theme.colors.text.primary }}>Settlement Status</h4>
                  <div className="flex space-x-2">
                    {settlementResult.data.status === 'confirmed' && (
                      <button
                        onClick={handleSettlementStatusPDFExport}
                        disabled={settlementPdfExporting}
                        className="px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-all duration-200 hover:shadow-lg"
                        style={{ background: theme.colors.background.buttonBg }}
                      >
                        {settlementPdfExporting ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <BiDownload className="w-4 h-4" />
                        )}
                        <span>{settlementPdfExporting ? 'Downloading...' : 'Download Receipt'}</span>
                      </button>
                    )}
                    <button
                      onClick={handleSettlementRefresh}
                      className="p-2 rounded-lg hover:bg-gray-700 transition-colors"
                      style={{ color: theme.colors.text.primary }}
                      title="New Search"
                    >
                      <BiRefresh className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.primary }}>
                      <label className="block text-sm font-medium mb-1" style={{ color: theme.colors.text.primary + '80' }}>Status</label>
                      <span className={`text-lg font-bold px-3 py-1 rounded-full text-sm ${
                        settlementResult.data.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                        settlementResult.data.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                        settlementResult.data.status === 'failed' ? 'bg-red-100 text-red-800' :
                        settlementResult.data.status === 'not_found' ? 'bg-yellow-100 text-yellow-800' :
                        settlementResult.data.status === 'server_error' ? 'bg-red-100 text-red-800' :
                        settlementResult.data.status === 'unauthorized' ? 'bg-orange-100 text-orange-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {settlementResult.data.status === 'confirmed' ? 'Confirmed' :
                         settlementResult.data.status === 'processing' ? 'Processing' :
                         settlementResult.data.status === 'failed' ? 'Failed' :
                         settlementResult.data.status === 'not_found' ? 'Not Found' :
                         settlementResult.data.status === 'server_error' ? 'Server Error' :
                         settlementResult.data.status === 'unauthorized' ? 'Unauthorized' :
                         settlementResult.data.status || 'Unknown'}
                      </span>
                      {sourceTable && (
                        <div className="mt-2">
                          <span className="text-xs" style={{ color: theme.colors.text.primary + '60' }}>
                            ({getSourceTableLabel(sourceTable)})
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Enhanced error information for failed transactions */}
                    {settlementResult.data.status === 'failed' && (
                      <>
                        {settlementResult.data.response_message && (
                          <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.primary }}>
                            <label className="block text-sm font-medium mb-1" style={{ color: theme.colors.text.primary + '80' }}>Response Message</label>
                            <span className="text-sm" style={{ color: theme.colors.text.primary }}>{settlementResult.data.response_message}</span>
                          </div>
                        )}
                        {settlementResult.data.narration && (
                          <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.primary }}>
                            <label className="block text-sm font-medium mb-1" style={{ color: theme.colors.text.primary + '80' }}>Description</label>
                            <span className="text-sm" style={{ color: theme.colors.text.primary }}>{settlementResult.data.narration}</span>
                          </div>
                        )}
                      </>
                    )}

                    <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.primary }}>
                      <label className="block text-sm font-medium mb-1" style={{ color: theme.colors.text.primary + '80' }}>Amount</label>
                      <span className="text-lg font-bold" style={{ color: settlementResult.data.amount === 'N/A' ? '#6b7280' : '#10b981' }}>
                        {settlementResult.data.amount === 'N/A' ? 'N/A' : `₦${settlementResult.data.amount?.toLocaleString()}`}
                      </span>
                    </div>

                    <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.primary }}>
                      <label className="block text-sm font-medium mb-1" style={{ color: theme.colors.text.primary + '80' }}>Charges</label>
                      <span className="text-sm" style={{ color: theme.colors.text.primary }}>
                        ₦{settlementResult.data.charges?.toLocaleString() || 'N/A'}
                      </span>
                    </div>

                    <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.primary }}>
                      <label className="block text-sm font-medium mb-1" style={{ color: theme.colors.text.primary + '80' }}>Transaction Type</label>
                      <span className="text-sm" style={{ color: theme.colors.text.primary }}>
                        {settlementResult.data.transaction_type?.charAt(0).toUpperCase() + settlementResult.data.transaction_type?.slice(1) || 'N/A'}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.primary }}>
                      <label className="block text-sm font-medium mb-1" style={{ color: theme.colors.text.primary + '80' }}>Transaction Reference</label>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-mono break-all" style={{ color: theme.colors.text.primary }}>{settlementResult.data.transaction_reference}</span>
                          <button
                            onClick={() => copyToClipboard(settlementResult.data.transaction_reference)}
                            className="p-1 hover:bg-gray-700 rounded transition-colors"
                            style={{ color: theme.colors.text.primary + '80' }}
                            title="Copy Reference"
                          >
                            {copied ? <BiCheck className="w-3 h-3" style={{ color: '#10b981' }} /> : <BiCopy className="w-3 h-3" />}
                          </button>
                        </div>
                        {(() => {
                          const refInfo = formatTransactionReference(settlementResult.data.transaction_reference);
                          if (refInfo.hasSuffix) {
                            return (
                              <div className="text-xs" style={{ color: theme.colors.text.primary + '60' }}>
                                <span className="font-medium">Extracted:</span> {refInfo.main}
                                <br />
                                <span className="font-medium">Suffix:</span> {refInfo.suffix}
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>

                    <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.primary }}>
                      <label className="block text-sm font-medium mb-1" style={{ color: theme.colors.text.primary + '80' }}>Destination Account</label>
                      <span className="text-sm font-mono" style={{ color: theme.colors.text.primary }}>{settlementResult.data.destination_account}</span>
                    </div>

                    <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.primary }}>
                      <label className="block text-sm font-medium mb-1" style={{ color: theme.colors.text.primary + '80' }}>Destination Account Name</label>
                      <span className="text-sm" style={{ color: theme.colors.text.primary }}>{settlementResult.data.destination_account_name || 'N/A'}</span>
                    </div>

                    <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.primary }}>
                      <label className="block text-sm font-medium mb-1" style={{ color: theme.colors.text.primary + '80' }}>Destination Bank</label>
                      <span className="text-sm" style={{ color: theme.colors.text.primary }}>{settlementResult.data.destination_bank}</span>
                    </div>

                    <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.primary }}>
                      <label className="block text-sm font-medium mb-1" style={{ color: theme.colors.text.primary + '80' }}>Partner ID</label>
                      <span className="text-sm font-mono" style={{ color: theme.colors.text.primary }}>{settlementResult.data.partner_id}</span>
                    </div>

                    <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.primary }}>
                      <label className="block text-sm font-medium mb-1" style={{ color: theme.colors.text.primary + '80' }}>Created At</label>
                      <span className="text-sm" style={{ color: theme.colors.text.primary }}>{formatDate(settlementResult.data.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { AccountStatusSearch };
